import { useState, useEffect, forwardRef } from "react";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface PopupBanner {
  id: string;
  title: string;
  file_url: string;
  is_active: boolean;
  display_duration: number;
}

const BannerOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
BannerOverlay.displayName = "BannerOverlay";

const BannerContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <BannerOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
BannerContent.displayName = "BannerContent";

const BannerPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeBanner, setActiveBanner] = useState<PopupBanner | null>(null);

  useEffect(() => {
    const fetchActiveBanner = async () => {
      try {
        const { data, error } = await supabase
          .from('popup_banners')
          .select('*')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();

        if (error || !data) {
          // Silently handle - no active banner is a valid state
          return;
        }

        setActiveBanner(data);

        // Show popup every time user visits the site
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 600);
        
        return () => clearTimeout(timer);
      } catch {
        // Silently handle network errors
      }
    };

    fetchActiveBanner();
  }, []);

  // Auto-close after display duration
  useEffect(() => {
    if (isOpen && activeBanner) {
      const duration = (activeBanner.display_duration || 10) * 1000;
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, activeBanner]);

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!activeBanner) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <BannerContent className="w-[90vw] max-w-[90vw] p-0 bg-transparent border-none rounded-lg overflow-hidden">
        {/* Close button - top left */}
        <button
          onClick={handleClose}
          className="absolute left-3 top-3 z-50 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
          aria-label="Close banner"
        >
          <X className="h-5 w-5" />
        </button>
        <img
          src={activeBanner.file_url}
          alt={activeBanner.title}
          className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
        />
      </BannerContent>
    </DialogPrimitive.Root>
  );
};

export default BannerPopup;
